import React, { useState } from 'react';
import axios from 'axios';
import { Config } from '../../config';

const SEVERITY_COLORS = {
  Critical: 'bg-red-900/40 text-red-300 border-red-800',
  High: 'bg-orange-900/40 text-orange-300 border-orange-800',
  Medium: 'bg-yellow-900/40 text-yellow-300 border-yellow-800',
  Low: 'bg-sky-900/40 text-sky-300 border-sky-800',
  Info: 'bg-slate-800/40 text-slate-300 border-slate-700',
};

function NormalizeTab({ engagement, onFindingsImported }) {
  const [rawText, setRawText] = useState('');
  const [source, setSource] = useState('pentagi');
  const [normalizing, setNormalizing] = useState(false);
  const [importing, setImporting] = useState(false);
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const token = localStorage.getItem('token');
  const headers = { access_token: `Bearer ${token}` };

  const handleNormalize = async () => {
    if (!rawText.trim()) { setError('Paste some text to normalize'); return; }
    setNormalizing(true);
    setError(null);
    setSuccess(null);
    setPreview(null);

    try {
      const res = await axios.post(
        `${Config.APIBaseURL}/engagement/offensive/${engagement.id}/findings/normalize`,
        { text: rawText, source },
        { headers, withCredentials: true }
      );
      setPreview(res.data);
      if (res.data.count === 0) {
        setError('No findings could be extracted from the text. Try providing more detailed assessment output.');
      }
    } catch (e) {
      setError(e.response?.data?.detail || 'Normalization failed');
    } finally {
      setNormalizing(false);
    }
  };

  const handleImport = async () => {
    if (!rawText.trim()) return;
    setImporting(true);
    setError(null);
    setSuccess(null);

    try {
      const res = await axios.post(
        `${Config.APIBaseURL}/engagement/offensive/${engagement.id}/findings/normalize-and-import`,
        { text: rawText, source },
        { headers, withCredentials: true }
      );
      setSuccess(`Imported ${res.data.count} findings successfully${res.data.skipped > 0 ? ` (${res.data.skipped} skipped)` : ''}`);
      setPreview(null);
      setRawText('');
      onFindingsImported && onFindingsImported();
    } catch (e) {
      setError(e.response?.data?.detail || 'Import failed');
    } finally {
      setImporting(false);
    }
  };

  return (
    <div className="space-y-4">
      <div>
        <p className="text-sm text-slate-400 mb-3">
          Paste raw assessment output from any source — PentAGI results, tool output, manual notes, scanner reports.
          The AI normalizer will extract structured findings matching your report format.
        </p>

        {/* Source selector */}
        <div className="flex gap-2 mb-3">
          <label className="text-xs text-slate-500 self-center mr-2">Source:</label>
          {['pentagi', 'manual', 'burp', 'nessus', 'other'].map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => setSource(s)}
              className={`px-2 py-1 text-xs rounded transition ${
                source === s
                  ? 'bg-indigo-600 text-white'
                  : 'bg-slate-800 text-slate-400 hover:bg-slate-700'
              }`}
            >
              {s === 'pentagi' ? 'PentAGI' : s === 'burp' ? 'Burp Suite' : s === 'nessus' ? 'Nessus' : s.charAt(0).toUpperCase() + s.slice(1)}
            </button>
          ))}
        </div>

        {/* Text input */}
        <textarea
          value={rawText}
          onChange={(e) => setRawText(e.target.value)}
          rows={12}
          placeholder="Paste raw assessment text here...&#10;&#10;Examples:&#10;- PentAGI task result text&#10;- Nmap scan output&#10;- Manual finding notes&#10;- Burp Suite issue export"
          className="w-full rounded-lg bg-slate-950/70 border border-slate-800 p-3 text-slate-200 text-sm font-mono focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 placeholder-slate-600"
        />

        <div className="flex items-center justify-between mt-3">
          <div className="text-xs text-slate-500">
            {rawText.length > 0 ? `${rawText.length.toLocaleString()} characters` : ''}
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={handleNormalize}
              disabled={normalizing || !rawText.trim()}
              className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white text-sm rounded-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {normalizing ? (
                <>
                  <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                  Analyzing...
                </>
              ) : 'Preview Findings'}
            </button>
            {preview && preview.count > 0 && (
              <button
                type="button"
                onClick={handleImport}
                disabled={importing}
                className="px-4 py-2 bg-emerald-700 hover:bg-emerald-800 text-white text-sm rounded-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {importing ? 'Importing...' : `Import ${preview.count} Findings`}
              </button>
            )}
          </div>
        </div>
      </div>

      {error && (
        <div className="p-3 rounded-lg bg-red-900/20 border border-red-800 text-red-300 text-sm">
          {error}
        </div>
      )}

      {success && (
        <div className="p-3 rounded-lg bg-emerald-900/20 border border-emerald-800 text-emerald-300 text-sm">
          {success}
        </div>
      )}

      {/* Preview results */}
      {preview && preview.findings && (
        <div className="space-y-3">
          <h4 className="text-xs font-semibold uppercase tracking-wider text-slate-400">
            Extracted Findings ({preview.count})
          </h4>
          <div className="max-h-[400px] overflow-y-auto space-y-2 pr-1">
            {preview.findings.map((finding, idx) => (
              <div key={idx} className="p-3 rounded-lg bg-slate-900/50 border border-slate-800">
                <div className="flex items-start justify-between gap-3 mb-2">
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-sm text-slate-200 truncate">
                      {finding.title}
                    </div>
                    <div className="text-xs text-slate-500 mt-0.5">
                      {finding.finding_type}
                      {finding.cwe_id && ` · ${finding.cwe_id}`}
                      {finding.owasp_category && ` · ${finding.owasp_category}`}
                    </div>
                  </div>
                  <span className={`px-2 py-0.5 rounded text-[10px] font-semibold border ${SEVERITY_COLORS[finding.severity] || SEVERITY_COLORS.Info}`}>
                    {finding.severity}
                  </span>
                </div>
                <p className="text-xs text-slate-400 line-clamp-3">{finding.description}</p>
                {finding.affected_assets && finding.affected_assets.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-1">
                    {finding.affected_assets.map((asset, i) => (
                      <span key={i} className="px-1.5 py-0.5 bg-slate-800 rounded text-[10px] text-slate-400 font-mono">
                        {asset}
                      </span>
                    ))}
                  </div>
                )}
                {finding.cvss_score && (
                  <div className="mt-1 text-[10px] text-slate-500">
                    CVSS: {finding.cvss_score}{finding.cvss_vector && ` (${finding.cvss_vector})`}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default NormalizeTab;
